import React, { useCallback, useMemo } from 'react'
import CardComponent from 'components/Card'
import CardUser from '../User'
import { useSelector } from 'react-redux'
import createCardStatusSelector from 'routes/Board/selectors/createCardStatusSelector'
import { useBoardContext } from 'dataProviders/Board'
import { createSelector } from 'reselect'
import createCardIssueLevelSelector from 'routes/Board/selectors/createCardIssueLevelSelector'
import createIsCardObservedSelector from 'selectors/createIsCardObservedSelector'
import * as CardStatusRoles from 'const/CardStatusRoles'
import SmartCard from 'components/SmartCard'

type Props = {
  id: number;
  cardComponent?: React.ComponentType<React.ComponentProps<typeof CardComponent>>;
}

const BoardCardComponent = React.forwardRef<any, Props>(({
  id,
  cardComponent: Card = CardComponent,

  ...restProps
}, ref) => {
  const boardContext = useBoardContext()

  const name = useSelector(state => state['view-board'].cards[id]?.name)

  const responsible = useSelector(state => state['view-board'].cards[id]?.responsible)

  const users = useSelector(state => state['view-board'].cards[id]?.users)
  
  const labelsSelector = useMemo(() => createSelector<any, any, any, any[]>(
    [
      (state, { cardId }) => state['view-board'].cards[cardId]?.labels || [],
      state => state['view-board'].labels.labelsById
    ],
    (labels, labelsById) =>
      labels
        .map(id => labelsById[id])
        .filter(label => !!label) // Filter non exisiting labels
        .sort((a, b) => a.position - b.position)
  ), [])
  const labels = useSelector<any, any>(state => labelsSelector(state, {
    cardId: id,
  }))

  const statusSelector = useMemo(() => createCardStatusSelector(
    (state, { cardId }) => cardId,
    (state, { boardId }) => state['view-board'].cardStatuses[boardId]?.ids || [],
  ), [])
  const status = useSelector<any, any>(state => statusSelector(state, {
    cardId: id,
    boardId: boardContext.id,
  }))

  const startDate = useSelector(state => state['view-board'].cards[id]?.startDate)

  const dueDate = useSelector(state => state['view-board'].cards[id]?.dueDate)

  const isObservedSelector = useMemo(() => createIsCardObservedSelector(), [])
  const isObserved = useSelector<any, any>(state => isObservedSelector(state, {
    cardId: id,
  }))

  const isArchived = useSelector(state => state['view-board'].cards[id]?.isArchived)

  const isTemplate = useSelector(state => state['view-board'].cards[id]?.isTemplate)

  const isBoardClosed = useSelector(state => state['view-board'].view[boardContext.id]?.isClosed)

  const unreadCount = useSelector<any, number>(state => state.kanbo.notifications.countsByCardId[id])

  const checklistItemCount = useSelector(state => state['view-board'].cards[id]?.checklistItemCount)
  
  const completedChecklistItemCount = useSelector(state => state['view-board'].cards[id]?.completedChecklistItemCount)
  
  const subcardsSelector = useMemo(() => createSelector<any, any, any, number[]>(
    [
      (state, { cardId }) => state.kanbo.cardRelations.byId[cardId]?.childIds || [],
      state => state['view-board'].cards,
    ],
    (childIds, cardsById) =>
      childIds.filter(childId =>
        cardsById[childId] &&
        !cardsById[childId].isArchived
      )
  ), [])
  const subcards = useSelector<any, number[]>(state => subcardsSelector(state, {
    cardId: id,
  }))

  const completedSubcardCountSelector = useMemo(() => createSelector<any, any, any, number>(
    [
      (state, { cardId }) => state.kanbo.cardRelations.byId[cardId]?.childIds || [],
      state => state['view-board'].cards,
      state => state['view-board'].cardStatuses.byId,
    ],
    (childIds, cardsById, cardStatusesById) =>
      childIds.filter(childId =>
        cardsById[childId] &&
        !cardsById[childId].isArchived &&
        cardStatusesById[cardsById[childId]?.statusId]?.role === CardStatusRoles.COMPLETED
      ).length
  ), [])
  const completedSubcardCount = useSelector<any, number>(state => completedSubcardCountSelector(state, {
    cardId: id,
  }))

  const issueLevelSelector = useMemo(() => createCardIssueLevelSelector(), [])
  const issueLevel = useSelector<any, any>(state => issueLevelSelector({
    cardId: id,
    blockerCount: state['view-board'].cards[id]?.blockerCount,
    isArchived: state['view-board'].cards[id]?.isArchived,
    isTemplate: state['view-board'].cards[id]?.isTemplate,
    dueDate: state['view-board'].cards[id]?.dueDate,
    statusId: state['view-board'].cards[id]?.statusId,
    isBoardClosed: state['view-board'].view[state['view-board'].cards[id]?.boardId]?.isClosed,
    cardStatusesById: state['view-board'].cardStatuses.byId,
    previousBlockedCards: state.kanbo.previousBlockedCards[id],
    descendantBlockedCards: state.kanbo.descendantBlockedCards[id],
    boardsById: state['view-board'].view,
    cardsById: state['view-board'].cards,
    cardRelations: state.kanbo.cardRelations.byId[id],
    descendantCardsDates: state['view-board'].cards[id]?.descendantCardsDates,
    previousCardsDates: state['view-board'].cards[id]?.previousCardsDates,
  }))

  const renderUser = useCallback((props) => {  
    return (
      <CardUser
        {...props}
        cardId={id}
        isLastMember={users.length === 0}
      />
    )
  }, [id, users])

  return (
    <Card
      {...restProps}
      ref={ref}
      id={id}
      userComponent={renderUser}
      issueLevel={issueLevel}
      name={name}
      responsible={responsible}
      users={users}
      labels={labels}
      status={status}
      startDate={startDate}
      dueDate={dueDate}
      isObserved={isObserved}
      isArchived={isArchived}
      isTemplate={isTemplate}
      isBoardClosed={isBoardClosed}
      unreadCount={unreadCount}
      checklistItemCount={checklistItemCount}
      completedChecklistItemCount={completedChecklistItemCount}
      subcards={subcards.map(subcardId =>
        <SmartCard
          key={subcardId}
          id={subcardId}
        />
      )}
      completedSubcardCount={completedSubcardCount}
    />
  )
})

export default BoardCardComponent